import { Check, ChevronDown, Search, X } from "lucide-react";
import { useId, useMemo, useState } from "react";

import { RotuloDeFiltro } from "@/components/RotuloDeFiltro";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface OpcionConConteo {
  valor: string;
  etiqueta: string;
  /** Cuántas fichas tiene. El conteo es lo que hace elegible una opción. */
  fichas: number;
}

interface SelectorMultipleProps {
  rotulo: string;
  opciones: OpcionConConteo[];
  seleccion: string[];
  onCambio: (seleccion: string[]) => void;
  /** El plural, que se usa en el resumen y en el buscador: "comunas", "portales". */
  plural: string;
  /**
   * ¿Todavía no llegaron las opciones?
   *
   * NO ES LO MISMO QUE NO TENER NINGUNA, y confundirlas costó caro: el catálogo
   * de transacciones tardaba segundos en poblarse y el menú decía "sin opciones"
   * mientras tanto. Eso se lee como una RESPUESTA y no como una espera.
   */
  cargando?: boolean;
  className?: string;
}

/**
 * A partir de cuántas opciones aparece el buscador.
 *
 * Con cinco o menos estorba: agrega un paso para leer una lista que entra
 * entera. La pestaña de transacciones tiene cuatro comunas y no lo necesita.
 */
const OPCIONES_PARA_BUSCAR = 5;

/**
 * Selector de varias opciones con buscador, conteos y resumen.
 *
 * VIVE EN `components/` Y NO EN `components/referencias/` porque dejó de ser
 * del módulo: es el control del sistema para elegir varios de una lista larga.
 *
 * TRES REGLAS QUE NO SE VEN EN EL MARKUP
 *
 * 1. LA SELECCIÓN VACÍA SIGNIFICA "TODAS", no "ninguna". Un control que en su
 *    estado inicial se lee como "sin elegir" hace dudar de si el listado está
 *    filtrado. Por eso el botón dice "Todas · N" y por eso "Todas", en el pie,
 *    LIMPIA la selección en vez de marcarlo todo: marcar las 255 una por una
 *    produce el mismo resultado con una url ilegible.
 *
 * 2. EL CONTEO NO ES DECORACIÓN: es lo que evita elegir un filtro que devuelve
 *    cero. Una opción sin fichas muestra "—" y su número va apagado — se puede
 *    elegir igual, pero se ve que no va a traer nada.
 *
 * 3. LO ELEGIDO SE MUESTRA ARRIBA, como fichas quitables, y ADEMÁS queda
 *    primero en la lista. En 255 comunas, lo que se acaba de marcar se iría
 *    fuera de la vista al bajar o al escribir la siguiente búsqueda: sin las
 *    fichas no habría forma de saber qué está puesto sin cerrar el menú.
 *
 * 4. LA LISTA VA ENTERA Y SCROLLEA. Antes se mostraban DOCE de 255 y el resto
 *    solo se alcanzaba escribiendo. Un tope obliga a saber de antemano el
 *    nombre de lo que se busca, y en un catálogo de comunas eso es justo lo
 *    que no se sabe: se reconoce al verlo. Lo que acota el menú es su alto,
 *    no cuántas filas entran en él.
 */
export function SelectorMultiple({
  rotulo,
  opciones,
  seleccion,
  onCambio,
  plural,
  cargando = false,
  className,
}: SelectorMultipleProps) {
  const rotuloId = useId();
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState("");

  /**
   * La selección con la que se ordenó la lista, CONGELADA al abrir el menú.
   *
   * Lo elegido va primero (regla 3), pero ordenar por la selección VIVA hacía
   * que cada clic reacomodara la lista debajo del puntero: marcar la comuna 40
   * la manda al tope y corre una fila todo lo que estaba abajo, así que el
   * clic siguiente cae sobre otra comuna. En 255 opciones eso no es una
   * molestia, es elegir mal sin notarlo.
   *
   * Congelada, la lista queda QUIETA mientras se elige y se reacomoda recién al
   * volver a abrir. No se pierde nada: qué está puesto ya lo dicen las fichas
   * quitables de la cabecera, que es para lo que existen.
   */
  const [ordenSegun, setOrdenSegun] = useState<string[]>(seleccion);

  const conBuscador = opciones.length > OPCIONES_PARA_BUSCAR;

  /** De más fichas a menos y, a igualdad, alfabético: el orden del diseño. */
  const ordenadas = useMemo(
    () =>
      [...opciones].sort(
        (a, b) => b.fichas - a.fichas || a.etiqueta.localeCompare(b.etiqueta, "es"),
      ),
    [opciones],
  );

  const visibles = useMemo(() => {
    const buscado = comparable(busqueda);

    if (buscado !== "") {
      return ordenadas.filter((o) => comparable(o.etiqueta).includes(buscado));
    }

    // LA LISTA VA ENTERA: lo que la vuelve manejable es el scroll, no un tope.
    // Solo se reordena, con lo elegido arriba — y según la selección CONGELADA
    // al abrir, para que marcar una opción no mueva las de abajo.
    return [
      ...ordenadas.filter((o) => ordenSegun.includes(o.valor)),
      ...ordenadas.filter((o) => !ordenSegun.includes(o.valor)),
    ];
    // `ordenSegun.join` y no el arreglo: quien llama arma `seleccion` con
    // `parametros.getAll(...)`, que devuelve uno NUEVO en cada render, así que
    // con el arreglo como dependencia el memo se invalidaba siempre y el orden
    // y el filtro se recalculaban sobre 255 opciones en cada tecla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordenadas, ordenSegun.join("|"), busqueda]);

  const alternar = (valor: string) => {
    onCambio(
      seleccion.includes(valor)
        ? seleccion.filter((v) => v !== valor)
        : [...seleccion, valor],
    );
  };

  const elegidas = ordenadas.filter((o) => seleccion.includes(o.valor));

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {/* El rótulo se ENLAZA con el disparador. Un `combobox` no toma su nombre
          del contenido —así lo define ARIA— así que un `<Label>` suelto al lado
          se ve pero no nombra nada: el control llegaba SIN nombre accesible a
          un lector de pantalla, y con dos selectores seguidos ("Comuna" y
          "Portal o corredor") no había forma de distinguirlos. */}
      <RotuloDeFiltro id={rotuloId}>{rotulo}</RotuloDeFiltro>

      <Popover
        open={abierto}
        onOpenChange={(valor) => {
          setAbierto(valor);
          // Al ABRIR se fija el orden: lo que ya está elegido sube al tope una
          // vez y de ahí no se mueve mientras dure la sesión de elección.
          if (valor) setOrdenSegun(seleccion);
          // La búsqueda se descarta al cerrar: al reabrir se espera la lista,
          // no lo que se tecleó hace media hora.
          if (!valor) setBusqueda("");
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-labelledby={rotuloId}
            aria-expanded={abierto}
            className={cn(
              "h-11 w-full justify-between gap-2 font-normal",
              // El borde dice el estado sin necesidad de abrir: verde mientras
              // está abierto, verde tenue con filtro puesto, neutro sin nada.
              abierto
                ? "border-primary"
                : seleccion.length > 0 && "border-primary/35",
            )}
          >
            <span
              className={cn(
                "truncate",
                seleccion.length > 0 ? "text-ink font-bold" : "text-ink-muted font-semibold",
              )}
            >
              {resumir(seleccion, ordenadas, cargando)}
            </span>

            {seleccion.length > 0 && (
              <span className="bg-accent text-accent-foreground flex-none rounded-md px-1.5 py-0.5 font-mono text-[0.65625rem] font-extrabold">
                {seleccion.length}
              </span>
            )}

            <ChevronDown
              className={cn(
                "text-ink-muted size-4 flex-none transition-transform",
                abierto && "rotate-180",
              )}
            />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          sideOffset={8}
          // EL ALTO ES LO QUE ACOTA EL MENÚ, ahora que la lista va entera. Se
          // toma el MENOR entre el alto del diseño y lo que Radix mide hasta
          // el borde de la ventana: con un tope fijo, un menú abierto abajo en
          // una pantalla baja se sale de la vista y su pie —donde vive
          // "Aplicar"— queda inalcanzable.
          className="flex max-h-[min(30rem,var(--radix-popover-content-available-height))] w-[19.5rem] flex-col overflow-hidden p-0"
        >
          {/* La cabecera NO scrollea: el buscador y las fichas de lo elegido se
              irían hacia arriba al bajar por la lista, justo cuando hacen falta
              para corregir. */}
          <div className="border-divisor flex flex-none flex-col gap-2.5 border-b p-3">
            {conBuscador && (
              <div className="relative">
                <Search
                  className="text-ink-hint pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2"
                  aria-hidden
                />
                <Input
                  autoFocus
                  value={busqueda}
                  onChange={(evento) => setBusqueda(evento.target.value)}
                  placeholder={`Buscar ${plural}…`}
                  aria-label={`Buscar ${plural}`}
                  className="h-9 pr-9 pl-9 text-xs font-semibold"
                />
                {busqueda !== "" && (
                  <button
                    type="button"
                    onClick={() => setBusqueda("")}
                    aria-label="Limpiar búsqueda"
                    className="text-ink-hint hover:text-ink-body absolute top-1/2 right-3 -translate-y-1/2"
                  >
                    <X className="size-3.5" />
                  </button>
                )}
              </div>
            )}

            {elegidas.length > 0 && (
              // LAS FICHAS TAMBIÉN SE ACOTAN. Con quince comunas marcadas
              // ocupan seis renglones y empujan la lista a cero: el menú del
              // que se elige se quedaría sin de dónde elegir.
              <ul className="scroll-fino flex max-h-[4.5rem] flex-wrap gap-1.5 overflow-y-auto">
                {elegidas.map((opcion) => (
                  <li
                    key={opcion.valor}
                    className="border-primary/30 bg-accent text-accent-foreground inline-flex max-w-full items-center gap-1.5 rounded-full border py-0.5 pr-1 pl-2.5 text-[0.6875rem] font-bold"
                  >
                    <span className="min-w-0 truncate">{opcion.etiqueta}</span>
                    <button
                      type="button"
                      onClick={() => alternar(opcion.valor)}
                      aria-label={`Quitar ${opcion.etiqueta}`}
                      className="flex-none opacity-65 hover:opacity-100"
                    >
                      <X className="size-3" strokeWidth={3} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <p className="text-ink-hint text-[0.65625rem] leading-relaxed font-bold tracking-[0.02em] text-pretty">
              {describirLaLista({
                total: ordenadas.length,
                conFichas: ordenadas.filter((o) => o.fichas > 0).length,
                elegidas: seleccion.length,
                coincidencias: visibles.length,
                buscando: comparable(busqueda) !== "",
                larga: conBuscador,
                cargando,
                plural,
              })}
            </p>
          </div>

          {/* `min-h-0` NO ES DECORACIÓN: sin él, un hijo de flex se niega a
              encogerse por debajo de su contenido, la lista empuja el menú
              hacia abajo y el scroll no llega a existir. Es la clase de la que
              depende todo lo demás de este bloque. */}
          <div className="scroll-fino min-h-0 flex-1 overflow-y-auto p-2">
            {visibles.map((opcion) => {
              const marcada = seleccion.includes(opcion.valor);

              return (
                <label
                  key={opcion.valor}
                  className={cn(
                    "flex cursor-pointer items-center gap-3 rounded-md px-2.5 py-2",
                    marcada ? "bg-success-soft/45" : "hover:bg-divisor-suave",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={marcada}
                    onChange={() => alternar(opcion.valor)}
                    className="peer sr-only"
                  />
                  {/* La casilla se dibuja, como el radio de la tabla del paso 1
                      y por lo mismo: el `accent-color` del navegador solo tiñe
                      la marca y deja el aro casi invisible. */}
                  <span
                    className={cn(
                      "flex size-5 flex-none items-center justify-center rounded-[0.375rem] border-[1.5px] transition-colors",
                      "peer-focus-visible:border-ring peer-focus-visible:ring-ring/50 peer-focus-visible:ring-[3px]",
                      marcada
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-input bg-background",
                    )}
                  >
                    {marcada && <Check className="size-3.5" strokeWidth={3.2} />}
                  </span>

                  <span className="text-ink-body min-w-0 flex-1 truncate text-xs font-semibold">
                    {opcion.etiqueta}
                  </span>

                  {/* SIN FICHAS SE MUESTRA UN GUION, no un cero: el cero se lee
                      como un dato medido y el guion como "acá no hay". La opción
                      se puede elegir igual — verla apagada es lo que evita
                      elegirla creyendo que traerá algo. */}
                  <span
                    className={cn(
                      "flex-none rounded-[0.3125rem] px-1.5 py-0.5 font-mono text-[0.65625rem] font-bold",
                      opcion.fichas > 0 ? "bg-divisor text-ink-muted" : "text-ink-ghost",
                    )}
                  >
                    {opcion.fichas > 0 ? opcion.fichas : "—"}
                  </span>
                </label>
              );
            })}

            {/* La nota de arriba ya dice si está cargando o si no hay nada, así
                que acá NO se repite: el mismo mensaje dos veces en un menú de
                288 px se lee como si fueran dos cosas distintas. Este bloque es
                solo para la búsqueda sin resultados, que la nota no explica. */}
            {visibles.length === 0 && !cargando && opciones.length > 0 && (
              <div className="flex flex-col gap-1 px-3 py-6 text-center">
                <p className="text-ink-body text-xs font-bold">Sin coincidencias</p>
                <p className="text-ink-hint text-[0.6875rem] font-semibold text-pretty">
                  Ninguna coincide con «{busqueda}».
                </p>
              </div>
            )}
          </div>

          <div className="border-divisor bg-superficie-sutil flex flex-none items-center gap-3 border-t px-3 py-2.5">
            {/* "Todas" LIMPIA, no marca todo: vacío ya significa todas, y marcar
                las 255 una por una produce el mismo resultado con una url
                ilegible. Por eso no hay además un "Quitar selección": en el
                diseño los dos hacen exactamente esto, y dos botones para una
                sola acción enseñan a no leerlos. */}
            <Button
              variant="ghost"
              size="sm"
              disabled={seleccion.length === 0}
              onClick={() => onCambio([])}
            >
              Todas
            </Button>

            <Button
              size="sm"
              variant="secondary"
              className="ml-auto"
              onClick={() => setAbierto(false)}
            >
              {seleccion.length > 0 ? `Aplicar · ${seleccion.length}` : "Listo"}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * Cómo se lee el botón según cuántas opciones haya elegidas.
 *
 * Con una sola se muestra su NOMBRE y no "1 comuna": el nombre es el dato, y el
 * número obligaría a abrir el menú para saber cuál es. Con varias se muestra la
 * primera y cuántas más, que es lo que entra en un control de 190 px.
 */
function resumir(
  seleccion: string[],
  opciones: OpcionConConteo[],
  cargando: boolean,
): string {
  if (seleccion.length === 0) {
    // "Todas · 0" mientras cargan afirma que no hay ninguna.
    return cargando ? "Todas" : `Todas · ${opciones.length}`;
  }

  const primera = opciones.find((o) => o.valor === seleccion[0]);

  // SIN LA OPCIÓN NO SE MUESTRA SU VALOR CRUDO: el botón decía "04301", que no
  // le dice nada a nadie. Pasa mientras el catálogo viaja y cuando lo elegido
  // pertenece a la otra pestaña.
  if (!primera) {
    return seleccion.length === 1 ? "1 elegida" : `${seleccion.length} elegidas`;
  }

  return seleccion.length === 1
    ? primera.etiqueta
    : `${primera.etiqueta} +${seleccion.length - 1}`;
}

/**
 * La línea que explica QUÉ se está viendo.
 *
 * Con la lista entera detrás de un scroll, el tamaño del catálogo deja de estar
 * a la vista: se ven seis filas de 255 y nada dice cuántas quedan abajo. La
 * nota lo dice, y de paso ofrece la salida rápida —buscar— antes de que alguien
 * recorra doscientas comunas con la rueda del mouse.
 */
function describirLaLista({
  total,
  conFichas,
  elegidas,
  coincidencias,
  buscando,
  larga,
  cargando,
  plural,
}: {
  total: number;
  conFichas: number;
  elegidas: number;
  coincidencias: number;
  buscando: boolean;
  /** ¿Tiene buscador? Es lo mismo que "no entra de un vistazo". */
  larga: boolean;
  cargando: boolean;
  plural: string;
}): string {
  if (cargando) return "Cargando opciones…";
  if (total === 0) return "Sin opciones para esta pestaña";

  if (buscando) {
    return coincidencias === 1
      ? `1 coincidencia de ${total}`
      : `${coincidencias} coincidencias de ${total}`;
  }

  // Con una lista corta la invitación a buscar sobra: no hay buscador al que
  // mandar a nadie. La pestaña de transacciones tiene cuatro comunas.
  if (!larga) {
    return total === 1 ? "1 opción" : `${total} opciones`;
  }

  const elegidasDice =
    elegidas === 0
      ? ""
      : `${elegidas} ${elegidas === 1 ? "seleccionada" : "seleccionadas"} · `;

  // "N CON REGISTROS" SOLO SI ALGUNA NO LOS TIENE. Con el catálogo actual todas
  // vienen con fichas, así que decirlo repetía el mismo número dos veces —
  // "255 con registros · busque entre 255 comunas"— y no informaba nada.
  const conRegistrosDice = conFichas < total ? `${conFichas} con registros · ` : "";

  return `${conRegistrosDice}${elegidasDice}busque entre ${total} ${plural}`;
}

/**
 * El texto reducido a lo comparable: sin acentos, sin mayúsculas.
 *
 * Buscar "nunoa" tiene que encontrar "Ñuñoa" y "curico" a "Curicó": son nombres
 * que nadie teclea con tilde cuando está apurado. Es el mismo criterio con el
 * que el servidor cruza las comunas.
 */
function comparable(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}
